import { useLayoutStore } from '@modules/core/store';
import { ReactNode, useEffect } from 'react';
import { Box, Text } from '@huspy/briks-web';
import { Skeleton } from '@mantine/core';
import { Outlet } from '@tanstack/react-router';
import useGetProposalById from '@modules/proposals/api/query/useGetProposalById';
import { ProposalCreationRoute } from '@modules/proposals/presentation/routes/index';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import useProposalCreationStateMachineEngine from '../hooks/useProposalCreationStateMachineEngine';
import ProposalCreationAsideContent from '../features/ProposalCreationAsideContent';
import { mapProposalEntityToLegacyProposalEntity } from '../views/ClientAndMortgage/mappers/mapProposalEntityToLegacyProposalEntity';
import { ProposalCreationContext } from '../context';

type Props = {
  children?: ReactNode,
};

const ProposalCreationLayoutAe = (_: Props) => {
  const { setAsideContent, setHeaderContent } = useLayoutStore();
  const { id } = ProposalCreationRoute.useParams();
  const { data, isLoading, isFetched } = useGetProposalById(id, mapProposalEntityToLegacyProposalEntity);

  const {
    _STATE_MACHINE, goToNextState, goToPreviousState, unlockNextStateAndNavigate, currentStateIndex,
  } = useProposalCreationStateMachineEngine({
    proposal: data,
    isEnabled: !!isFetched,
  });

  useEffect(() => {
    setHeaderContent(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setAsideContent(
      <ProposalCreationAsideContent
        routeId={ id }
        isLoading={ isLoading }
        stateMachine={ _STATE_MACHINE }
      />
    );

    return () => {
      setAsideContent(null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, currentStateIndex, _STATE_MACHINE]);

  useEffect(() => {
    if (id && isFetched && !!data?.external_id) {
      trackAmplitudeEvent(USER_EVENTS.PROPOSAL.PROPOSAL_EDIT_STARTED, {
        useContext: true,
        addToContext: true,
        proposal_id: id,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetched]);

  return (
    isLoading ? (
      <Box px='100' py='50'>
        <Skeleton w='100%' height={ 500 } radius='8px' />
      </Box>
    )
      : (
        <Box h='100%'>
          {/* eslint-disable-next-line react/jsx-no-constructed-context-values */}
          <ProposalCreationContext.Provider value={ {
            goToNextState,
            goToPreviousState,
            unlockNextStateAndNavigate,
          } }
          >
            {data ? (<Outlet />) : <Text>No proposal found</Text>}
          </ProposalCreationContext.Provider>
        </Box>
      )

  );
};

export default ProposalCreationLayoutAe;
